import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ResponsivePopover } from '../ResponsivePopover';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * ViewNameDialog — the one place a rep types a view's name (U2a).
 *
 * It replaces the four `window.prompt`s the views surface used to open (new
 * view, save-as-new, save-as-new-before-copy-link, rename). A native prompt
 * is unstyled, unthemeable (it ignores dark mode entirely), unsuppressable
 * in some browsers, and — the reason this became a build item — it looked
 * like a browser malfunction rather than part of the product.
 *
 * ── Ownership split ──────────────────────────────────────────────────────
 * This component owns the UI and NOTHING else. It resolves a name (or `null`
 * when the rep backs out) and the caller does the write — `saveAsNew(name)`,
 * `renameView(view, name)`. That is what lets `useViewStateMachine` stay a
 * pure state machine over the wire, with no DOM API in it, and what lets the
 * same dialog serve create and rename without knowing either operation.
 *
 * ── Promise, not a callback tree ─────────────────────────────────────────
 * `requestName()` returns a promise so a call site reads exactly like the
 * `window.prompt` line it replaces:
 *
 *   const name = await requestName({ title: 'Name this view' });
 *   if (name === null) return;
 *
 * `null` is the cancelled/dismissed case in every path — the Cancel button,
 * Escape, an outside click (both handled by `ResponsivePopover`), and unmount
 * with a request still open. A caller must never be left awaiting a promise
 * that can no longer settle.
 *
 * ── Built on ResponsivePopover ───────────────────────────────────────────
 * Same container the filters/columns popovers use, so this inherits the
 * anchored-on-desktop / bottom-sheet-under-720px behaviour, the portal (it
 * escapes any `overflow-hidden` ancestor), Escape, outside-click dismissal,
 * and — the reason it is not the shared `ui/Modal` — correct dark styling.
 * Semantic tokens in this app carry NO `.dark` overrides, so every surface
 * needs explicit `dark:` variants (see `FlowTable.tsx`'s convention note);
 * `ui/Modal` has none, which is exactly the U3 bug.
 *
 * ── Draft text and re-renders ────────────────────────────────────────────
 * The input's value lives in `NameForm`, which is REMOUNTED per request
 * (`key={request.key}`), so the seed is a `useState` initialiser rather than
 * an effect that re-syncs from props. Nothing can reseed it mid-edit, which
 * is the same guarantee `useDraftState` gives editors that are seeded from
 * server data — without the machinery, because a name request carries its
 * seed once, at open time, and never updates.
 */

export interface ViewNameRequest {
  /** Dialog heading, and the accessible name of the dialog region. */
  title: string;
  /** Suggested name, pre-filled and pre-selected. */
  initialValue?: string;
  /** Primary button label. Defaults to "Save". */
  confirmLabel?: string;
}

/** Resolves to the trimmed name, or `null` if the rep backed out. */
export type RequestViewName = (request: ViewNameRequest) => Promise<string | null>;

export interface ViewNameDialogHandle {
  requestName: RequestViewName;
  /** Render this wherever the host component's own markup lives. */
  dialog: ReactNode;
}

interface PendingRequest extends ViewNameRequest {
  /** Remount key — a fresh request must never inherit the last one's text. */
  key: number;
}

function NameForm({
  title,
  initialValue,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  initialValue: string;
  confirmLabel: string;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();

  return (
    <form
      className="flex w-64 flex-col gap-2 p-3"
      onSubmit={e => {
        e.preventDefault();
        if (trimmed.length === 0) return;
        onConfirm(trimmed);
      }}
    >
      <p className="text-[12px] font-semibold text-content-primary dark:text-nightshade-100">{title}</p>
      <input
        {...AUTOFILL_IGNORE_PROPS}
        autoFocus
        value={value}
        aria-label="View name"
        placeholder="View name"
        onChange={e => setValue(e.target.value)}
        onFocus={e => e.currentTarget.select()}
        className="w-full rounded-md border border-secondary bg-white px-2 py-1 text-[12px] text-content-primary outline-none placeholder:text-content-placeholder focus:ring-1 focus:ring-content-action dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
      />
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-secondary bg-white px-2 py-1 text-[12px] text-content-secondary hover:bg-bg-secondary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-400 dark:hover:bg-nightshade-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={trimmed.length === 0}
          className="rounded-md border border-content-action bg-content-action px-2 py-1 text-[12px] font-medium text-white hover:bg-content-action-hover disabled:opacity-50 dark:border-blueberry-500 dark:bg-blueberry-500"
        >
          {confirmLabel}
        </button>
      </div>
    </form>
  );
}

/**
 * Hosts one name dialog for a component. Returns the `requestName` promise
 * API and the node to render; anchor it to whatever the rep clicked (the
 * switcher trigger, the dirty box, a modal footer).
 */
export function useViewNameDialog(anchorRef: RefObject<HTMLElement | null>): ViewNameDialogHandle {
  const [request, setRequest] = useState<PendingRequest | null>(null);
  const resolveRef = useRef<((value: string | null) => void) | null>(null);
  const keyRef = useRef(0);

  const settle = useCallback((value: string | null) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setRequest(null);
    resolve?.(value);
  }, []);

  const requestName = useCallback<RequestViewName>(next => new Promise<string | null>(resolve => {
    // A second request while one is open (not reachable through today's UI,
    // but cheap to make honest): the earlier caller is told "cancelled"
    // rather than left awaiting a promise nothing will ever settle.
    resolveRef.current?.(null);
    resolveRef.current = resolve;
    keyRef.current += 1;
    setRequest({ ...next, key: keyRef.current });
  }), []);

  // Unmounting with a request open — e.g. the hosting modal closes — settles
  // it as cancelled for the same reason.
  useEffect(() => () => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(null);
  }, []);

  const cancel = useCallback(() => settle(null), [settle]);

  const dialog = (
    <ResponsivePopover
      open={request !== null}
      onClose={cancel}
      anchorRef={anchorRef}
      ariaLabel={request?.title ?? 'Name view'}
      className="w-64"
    >
      {request !== null && (
        <NameForm
          key={request.key}
          title={request.title}
          initialValue={request.initialValue ?? ''}
          confirmLabel={request.confirmLabel ?? 'Save'}
          onConfirm={name => settle(name)}
          onCancel={cancel}
        />
      )}
    </ResponsivePopover>
  );

  return { requestName, dialog };
}
